import React from 'react';
import { Box, Typography } from '@mui/material';
import MemoryOverviewTab from './MemoryOverviewTab';
import MemoryFilesTab from './MemoryFilesTab';
import MemoryInformationTab from './MemoryInformationTab';
import MemoryReviewTab from './MemoryReviewTab';
import { SparkDialog, SparkTabs } from 'sharedComponents/UI';
import { TabItem } from 'sharedComponents/UI/SparkTabs';
import MemoryCardHeader from './MemoryCardHeader';
import MemoryPopupFooter from './MemoryPopupFooter';
import RecentQuestionsTab from './RecentQuestionsTab';
import { useCurrentUser } from 'hooks/common/useCurrentUser';
import { useAddUserFlag } from 'hooks/userFlags/useAddUserFlag';
import { useFlagReasons } from 'hooks/EntityTypes/useFlagReasons';
import {
  useCreateFavouriteList,
  useDeleteMemoryFavourite,
} from 'hooks/Favourites';
import { useCreateMemoryFavourite } from 'hooks/Favourites/useCreateMemoryFavourite';
import { useMemoryPrivacyTypes } from 'hooks/EntityTypes/useMemoryPrivacyTypes';
import type { CreateFavouriteListFormValues } from 'components/common/Favourite/CreateFavouriteListPopUp';
import type { SparkReportFormValues } from 'components/common/Report/SparkReport';
import { useMemoryByID } from 'hooks/memory/useMemoryByID';
import SparkBadges from 'components/common/SparkBadges';
import { getGropedSparkBadges } from 'helpers/groupSparkBadges';
import SparkPopupHeaderSkeleton from 'sharedComponents/Skeleton/SparkPopupHeaderSkeleton';
import CardContentSkeleton from 'sharedComponents/Skeleton/CardContentSkeleton';
import { useUpdateMemory } from 'hooks/memory/useUpdateMemory';
import { getCurrentDateISOString } from 'utilities/time';
import { useCurrentRoute } from 'hooks/common';

type MemoryCardModalProps = {
  isMemoryCardDialogOpen: boolean;
  closeMemoryCardDialog: () => void;
  memoryID: string;
};

function MemoryCardModal({
  isMemoryCardDialogOpen,
  closeMemoryCardDialog,
  memoryID,
}: MemoryCardModalProps) {
  const { currentUserID } = useCurrentUser();
  const {
    isFetching,
    joinedMemory,
    isLoading: isLoadingMemory,
  } = useMemoryByID(memoryID);

  const { mutateAsync: createFavoriteList } = useCreateFavouriteList();
  const {
    mutateAsync: createMemoryFavorites,
    isLoading: isCreatingMemoryFavourite,
  } = useCreateMemoryFavourite(memoryID);
  const { isMemoryDialog } = useCurrentRoute();
  const {
    mutateAsync: deleteMemoryFavorites,
    isLoading: isDeletingMemoryFavourite,
  } = useDeleteMemoryFavourite(memoryID);

  const { memoryPrivacyTypeIdByNameMap } = useMemoryPrivacyTypes();
  const { mutateAsync: updateMemory } = useUpdateMemory(memoryID);

  const { mutateAsync: addUserFlag } = useAddUserFlag();
  const { dropdownOptions: flagReasonsDropdownOptions } = useFlagReasons();

  const { files, memorySearchResults, memoryRatings } = joinedMemory || {};

  const isPublicMemory = React.useMemo(() => {
    if (!joinedMemory || !memoryPrivacyTypeIdByNameMap) {
      return false;
    }
    return (
      joinedMemory.attributes.memoryPrivacyTypeID ===
      memoryPrivacyTypeIdByNameMap['Public']
    );
  }, [joinedMemory, memoryPrivacyTypeIdByNameMap]);

  const existingFavouriteList = joinedMemory?.meta.user.favouriteLists;
  const memoryFavourites = joinedMemory?.memoryFavourite;

  const tabItems: TabItem[] = React.useMemo(() => {
    return [
      {
        label: 'Overview',
        content: <MemoryOverviewTab joinedMemory={joinedMemory} />,
      },

      ...(memorySearchResults?.length
        ? [
            {
              label: 'Recent Questions',
              content: (
                <RecentQuestionsTab memorySearchResults={memorySearchResults} />
              ),
            },
          ]
        : []),
      ...(files?.length
        ? [
            {
              label: 'Files',
              content: <MemoryFilesTab files={files} />,
            },
          ]
        : []),
      //NOTE: Commenting out Reviews and Information for now This will be implemented in future

      // ...(joinedMemory
      //   ? [
      //       {
      //         label: 'Reviews',
      //         content: <MemoryReviewTab memory={joinedMemory} />,
      //       },
      //     ]
      //   : []),
      // {
      //   label: 'Information',
      //   content: <MemoryInformationTab joinedMemory={joinedMemory} />,
      // },
    ];
  }, [files, joinedMemory, memorySearchResults]);

  const handleCreateList = async (values: CreateFavouriteListFormValues) => {
    const res = await createFavoriteList(values.listName);

    const memoryFavourite = {
      memoryID: joinedMemory?.id,
      userID: currentUserID,
      favouriteListID: Number(res.data.id),
    };
    await createMemoryFavorites(memoryFavourite);
  };

  const handleFavouriteListItem = async (listID: number) => {
    const foundExistingFavouriteList = memoryFavourites?.find(
      (favourite) => favourite.attributes.favouriteListID === listID
    );
    if (foundExistingFavouriteList) {
      await deleteMemoryFavorites(`${foundExistingFavouriteList.id}`);
      return;
    }
    const memoryFavourite = {
      memoryID: joinedMemory?.id,
      userID: currentUserID,
      favouriteListID: listID,
    };
    await createMemoryFavorites(memoryFavourite);
  };

  const handleSendReport = async (values: SparkReportFormValues) => {
    const userFlag = {
      flagReasonID: +values.reasonID,
      memoryID: joinedMemory?.id,
      comments: values.comments,
    };
    await addUserFlag(userFlag);
  };
  const handleDeleteMemory = async () => {
    await updateMemory({
      deletedAt: getCurrentDateISOString(),
      isDeleted: true,
    });
  };

  const handleArchiveMemory = async () => {
    await updateMemory({
      isArchived: true,
    });
  };
  const userPermissions = joinedMemory?.meta.user.permissions;
  const isFavouriteMemoryTaskLoading =
    isDeletingMemoryFavourite || isCreatingMemoryFavourite || isFetching;
  const badgesCategoryMap = getGropedSparkBadges(joinedMemory?.meta?.badges);

  return (
    <SparkDialog
      open={isMemoryCardDialogOpen}
      onClose={closeMemoryCardDialog}
      title={''}
      sparkDialogHeader={
        isLoadingMemory ? (
          <SparkPopupHeaderSkeleton />
        ) : (
          <MemoryCardHeader
            joinedMemory={joinedMemory}
            onCreateList={handleCreateList}
            onFavouriteItemClick={handleFavouriteListItem}
            existingFavouriteList={existingFavouriteList || []}
            isLoading={isFavouriteMemoryTaskLoading}
            flagReasonsDropdownOptions={flagReasonsDropdownOptions}
            handleSendReport={handleSendReport}
            onArchive={handleArchiveMemory}
            onDelete={handleDeleteMemory}
            userPermissions={userPermissions}
          />
        )
      }
      titleContainerStyle={{ height: '2rem' }}
      sx={(theme) => ({
        '& .MuiDialogContent-root': {
          padding: 4,
          paddingTop: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          [theme.breakpoints.down(1000)]: {
            padding: 2,
          },
        },
      })}
    >
      {isLoadingMemory ? (
        <CardContentSkeleton />
      ) : (
        <Box>
          <Box
            marginBottom={2}
            display='flex'
            flexDirection='column'
            paddingTop={'10px'}
          >
            <SparkBadges badges={badgesCategoryMap?.['Info'] || []} />
            <Typography
              sx={{
                fontSize: '16px',
                fontStyle: 'normal',
                background: '#F5F5F5',
                borderRadius: '6px',
                whiteSpace: 'pre-line',
                padding: 1,
              }}
            >
              {joinedMemory?.attributes.description}
            </Typography>
          </Box>
          <SparkTabs tabItems={tabItems} />
          <MemoryPopupFooter
            joinedMemory={joinedMemory}
            closeMemoryCardDialog={closeMemoryCardDialog}
            isMemoryDialog={isMemoryDialog}
          />
        </Box>
      )}
    </SparkDialog>
  );
}

export default MemoryCardModal;
